/* eslint-disable max-len */
import { Form, Formik } from 'formik';
import React, { useContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { Panel } from 'primereact/panel';
import { defineMessages, useIntl } from 'react-intl';
import * as Yup from 'yup';
import gql from 'graphql-tag';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { useSelector } from 'react-redux';
import { Loading } from '../../components/Loading';
import { growlErrors } from '../../utils/NotificationUtil';
import { NotificationContext } from '../../components/GrowlNotifications';
import { useHistory } from 'react-router-dom';
import ChangeSubscriptionNotice from './ChangeSubscriptionNotice';
import PresentSubscription from './PresentSubscription';
import NewSubscriptionOptions from './NewSubscriptionOptions';
import NewSubscriptionPrices from './NewSubscriptionPrices';

const LOAD_DATA_ACTION_TYPE = 'LOAD_DATA';
const SET_COMPUTED_SUBSCRIPTION_COST = 'SET_COMPUTED_SUBSCRIPTION_COST';

const CONFIG_QUERY = gql`
  query configQuery($accountId: ID!) {
    availableNumbersOfEmployees(id: $accountId)
    availableSubscriptionPeriods {
      id
      duration
    }
  }
`;

const CHANGE_SUBSCRIPTION_MUTATION = gql`
  mutation changeSubscriptionMutation($input: ChangeSubscriptionInput!) {
    changeSubscription(input: $input) {
      account {
        id
      }
    }
  }
`;

const COMPUTE_SUBSCRIPTION_COST_MUTATION = gql`
  mutation computeSubscriptionCostMutation($input: ComputeSubscriptionCostInput!) {
    computeSubscriptionCost(input: $input) {
      computedCost
      computedCostBeforeGscDiscountForClient
      discountPercentage
    }
  }
`;

const i18nMessages = defineMessages({
  changeNoLimit: {
    id: 'account.subscription.change-no-limit',
    defaultMessage: 'Change NoLimit Subscription',
  },
});

const ChangeSubscription = ({ managingAccount, clientAccount }) => {
  const { id: managingAccountId } = managingAccount;

  const {
    id: clientAccountId,
    subscriptionInformation: {
      currentSubscription: {
        numberOfEmployees: currentSubscriptionNumberOfEmployees,
        subscriptionPeriodId: currentSubscriptionPeriodId,
      },
    },
  } = clientAccount;

  const history = useHistory();
  const { formatMessage } = useIntl();
  const growl = useContext(NotificationContext);

  const {
    cldr: { localeDisplayName },
  } = useSelector((state) => state.i18n);

  const [changeSubscriptionMutation, { loading: changeSubscriptionLoading }] = useMutation(
    CHANGE_SUBSCRIPTION_MUTATION,
  );
  const [computeSubscriptionCostMutation] = useMutation(COMPUTE_SUBSCRIPTION_COST_MUTATION);

  const initialState = {
    sendPaymentLinkDialogVisible: false,
    numbersOfEmployees: [],
    subscriptionPeriods: [],
    countryIndices: [],
    subscriptionCost: 0,
    subscriptionCostBeforeGscDiscount: 0,
    discountPercentage: 0,
  };

  const reducer = (state, action) => {
    switch (action.type) {
      case LOAD_DATA_ACTION_TYPE:
        return {
          ...state,
          numbersOfEmployees: action.payload.numbersOfEmployees,
          subscriptionPeriods: action.payload.subscriptionPeriods,
          countryIndices: action.payload.countryIndices,
        };
      case SET_COMPUTED_SUBSCRIPTION_COST:
        return {
          ...state,
          subscriptionCost: action.payload.subscriptionCost,
          subscriptionCostBeforeGscDiscount: action.payload.subscriptionCostBeforeGscDiscount,
          discountPercentage: action.payload.discountPercentage,
        };
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const {
    numbersOfEmployees,
    subscriptionPeriods,
    subscriptionCost,
    subscriptionCostBeforeGscDiscount,
    discountPercentage,
  } = state;

  const { loading, error, data } = useQuery(CONFIG_QUERY, {
    variables: {
      accountId: clientAccountId,
    },
  });

  const computeSubscriptionCost = async (numberOfEmployees, subscriptionPeriodId) => {
    try {
      const {
        data: {
          computeSubscriptionCost: { computedCost, computedCostBeforeGscDiscountForClient, discountPercentage },
        },
      } = await computeSubscriptionCostMutation({
        variables: {
          input: {
            managingAccountId,
            clientAccountId,
            numberOfEmployees,
            subscriptionPeriodId,
          },
        },
      });

      dispatch({
        type: SET_COMPUTED_SUBSCRIPTION_COST,
        payload: {
          subscriptionCost: computedCost,
          subscriptionCostBeforeGscDiscount: computedCostBeforeGscDiscountForClient,
          discountPercentage,
        },
      });
    } catch (mutationError) {
      growlErrors(growl, mutationError);
    }
  };

  const { availableNumbersOfEmployees, availableSubscriptionPeriods } = data;

  useEffect(() => {
    const dataIsAvailable =
      availableNumbersOfEmployees &&
      availableNumbersOfEmployees.length > 0 &&
      availableSubscriptionPeriods.length > 0 &&
      availableSubscriptionPeriods;

    if (dataIsAvailable) {
      const action = {
        type: LOAD_DATA_ACTION_TYPE,
        payload: {
          numbersOfEmployees: availableNumbersOfEmployees,
          subscriptionPeriods: availableSubscriptionPeriods,
        },
      };

      dispatch(action);

      computeSubscriptionCost(currentSubscriptionNumberOfEmployees, currentSubscriptionPeriodId);
    }
    // eslint-disable-next-line
  }, [availableNumbersOfEmployees, availableSubscriptionPeriods, error, localeDisplayName]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    growlErrors(growl, error);
  }

  const initialValues = {
    numberOfEmployees: currentSubscriptionNumberOfEmployees,
    subscriptionPeriodId: currentSubscriptionPeriodId,
  };

  const validationSchema = Yup.object().shape({
    numberOfEmployees: Yup.string().required(),
    subscriptionPeriodId: Yup.string().required(),
  });

  const changeSubscription = async (values, resetForm) => {
    try {
      await changeSubscriptionMutation({
        variables: {
          input: {
            numberOfEmployees: values.numberOfEmployees,
            subscriptionPeriodId: values.subscriptionPeriodId,
            managingAccountId,
            clientAccountId,
            sum: subscriptionCost,
          },
        },
      });

      history.go(-3);
    } catch (mutationError) {
      growlErrors(growl, mutationError);
      resetForm();
      computeSubscriptionCost(currentSubscriptionNumberOfEmployees, currentSubscriptionPeriodId);
    }
  };

  return (
    <>
      <ChangeSubscriptionNotice />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { resetForm }) => {
          changeSubscription(values, resetForm);
        }}
      >
        {({ handleSubmit }) => {
          return (
            <div className="buy-subscription-container">
              <Panel header={formatMessage(i18nMessages.changeNoLimit)}>
                <PresentSubscription clientAccount={clientAccount} />
                <Form onSubmit={handleSubmit}>
                  <NewSubscriptionOptions
                    numbersOfEmployees={numbersOfEmployees}
                    subscriptionPeriods={subscriptionPeriods}
                    clientAccount={clientAccount}
                    computeSubscriptionCost={computeSubscriptionCost}
                  />
                  <NewSubscriptionPrices
                    subscriptionCost={subscriptionCost}
                    subscriptionCostBeforeGscDiscount={subscriptionCostBeforeGscDiscount}
                    discountPercentage={discountPercentage}
                    managingAccount={managingAccount}
                    clientAccount={clientAccount}
                    changeSubscriptionLoading={changeSubscriptionLoading}
                  />
                </Form>
              </Panel>
            </div>
          );
        }}
      </Formik>
    </>
  );
};

ChangeSubscription.propTypes = {
  managingAccount: PropTypes.string.isRequired,
  clientAccount: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default ChangeSubscription;
